/**
 * ProfilePanel -- displays and allows editing of user profile settings.
 *
 * Shows:
 * - Name (editable)
 * - Email (read-only)
 * - Timezone (editable)
 * - Working hours (editable)
 * - Projects list (read-only)
 * - Notification preferences (toggleable)
 *
 * Edits are sent via onUpdateField which triggers chat messages.
 */

import { useState } from "react";
import type { UserProfile } from "./types";

interface ProfilePanelProps {
  profile: UserProfile | null;
  onUpdateField: (field: string, value: string) => void;
  onCreateProject?: (name: string) => void;
  isLoading?: boolean;
  savingField?: string | null;
}

interface EditingState {
  field: string | null;
  value: string;
}

export function ProfilePanel({
  profile,
  onUpdateField,
  onCreateProject,
  isLoading,
  savingField,
}: ProfilePanelProps): React.ReactElement {
  const [editing, setEditing] = useState<EditingState>({ field: null, value: "" });
  const [newProjectName, setNewProjectName] = useState("");

  const startEditing = (field: string, currentValue: string) => {
    setEditing({ field, value: currentValue });
  };

  const saveEdit = () => {
    if (editing.field && editing.value.trim()) {
      onUpdateField(editing.field, editing.value.trim());
    }
    setEditing({ field: null, value: "" });
  };

  const cancelEdit = () => {
    setEditing({ field: null, value: "" });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      saveEdit();
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  // Loading state
  if (isLoading && !profile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-text-tertiary text-sm">Loading profile...</p>
      </div>
    );
  }

  // Empty state
  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center">
          <svg
            className="w-6 h-6 text-text-tertiary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
            />
          </svg>
        </div>
        <p className="text-text-tertiary text-sm">Set up your profile via chat</p>
        <p className="text-text-tertiary text-xs">Try: "My name is John and I work 9-5 EST"</p>
      </div>
    );
  }

  const EditableField = ({
    label,
    field,
    value,
    placeholder,
  }: {
    label: string;
    field: string;
    value: string | undefined;
    placeholder: string;
  }) => {
    const isEditing = editing.field === field;
    const isSaving = savingField === field;

    return (
      <div className="flex items-center justify-between py-2 group">
        <span className="text-xs text-text-tertiary w-24 flex-shrink-0">{label}</span>
        {isEditing ? (
          <input
            type="text"
            value={editing.value}
            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            className="flex-1 text-sm text-text-primary bg-surface border border-border rounded px-2 py-1 focus:outline-none focus:border-primary"
            autoFocus
            placeholder={placeholder}
          />
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm text-text-primary truncate flex-1">
              {value || <span className="text-text-tertiary italic">Not set</span>}
            </span>
            {isSaving ? (
              <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <button
                type="button"
                onClick={() => startEditing(field, value || "")}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-surface-hover transition-opacity"
                title="Edit"
              >
                <svg
                  className="w-3.5 h-3.5 text-text-tertiary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const TimeField = ({
    label,
    field,
    value,
  }: {
    label: string;
    field: string;
    value: string | undefined;
  }) => {
    const isEditing = editing.field === field;

    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-tertiary">{label}</span>
        {isEditing ? (
          <input
            type="time"
            value={editing.value}
            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            className="text-sm text-text-primary bg-surface border border-border rounded px-2 py-1 focus:outline-none focus:border-primary"
            autoFocus
          />
        ) : (
          <button
            type="button"
            onClick={() => startEditing(field, value || "")}
            className="text-sm text-text-primary hover:text-primary transition-colors"
          >
            {value || "--:--"}
          </button>
        )}
      </div>
    );
  };

  const ToggleField = ({
    label,
    field,
    checked,
  }: {
    label: string;
    field: string;
    checked: boolean;
  }) => {
    return (
      <label className="flex items-center justify-between py-1.5 cursor-pointer group">
        <span className="text-sm text-text-primary">{label}</span>
        <button
          type="button"
          onClick={() => onUpdateField(field, String(!checked))}
          className={[
            "relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0",
            checked ? "bg-primary" : "bg-surface-hover",
          ].join(" ")}
        >
          <span
            className={[
              "absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200",
              checked ? "translate-x-[16px]" : "translate-x-0",
            ].join(" ")}
          />
        </button>
      </label>
    );
  };

  return (
    <div className="space-y-4">
      {/* Profile Section */}
      <div>
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
          Profile
        </h3>
        <div className="space-y-1 border-b border-border pb-3">
          {/* Avatar placeholder */}
          <div className="flex items-center gap-3 py-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-semibold">
                {profile.name?.charAt(0).toUpperCase() || "?"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {profile.name || "Set your name"}
              </p>
              <p className="text-xs text-text-tertiary truncate">
                {profile.email || "No email set"}
              </p>
            </div>
          </div>

          <EditableField
            label="Name"
            field="name"
            value={profile.name}
            placeholder="Your name"
          />
          <div className="flex items-center justify-between py-2">
            <span className="text-xs text-text-tertiary w-24 flex-shrink-0">Email</span>
            <span className="text-sm text-text-tertiary italic truncate flex-1">
              {profile.email || "Synced from account"}
            </span>
          </div>
        </div>
      </div>

      {/* Settings Section */}
      <div>
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
          Settings
        </h3>
        <div className="space-y-1 border-b border-border pb-3">
          <EditableField
            label="Timezone"
            field="timezone"
            value={profile.timezone}
            placeholder="e.g., America/New_York"
          />
          <div className="py-2">
            <span className="text-xs text-text-tertiary block mb-2">Working Hours</span>
            <div className="flex items-center gap-3">
              <TimeField
                label="Start"
                field="workingHours.start"
                value={profile.workingHours?.start}
              />
              <span className="text-text-tertiary">-</span>
              <TimeField
                label="End"
                field="workingHours.end"
                value={profile.workingHours?.end}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div>
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
          Projects
        </h3>
        <div className="space-y-1 border-b border-border pb-3">
          {profile.projects && profile.projects.length > 0 && (
            profile.projects.map((project) => (
              <div
                key={project.id}
                className="flex items-center gap-2 py-1.5 text-sm text-text-primary"
              >
                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="truncate">{project.name}</span>
              </div>
            ))
          )}
          {onCreateProject && (
            <div className="flex items-center gap-2 pt-1">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newProjectName.trim()) {
                    onCreateProject(newProjectName.trim());
                    setNewProjectName("");
                  }
                  if (e.key === "Escape") {
                    setNewProjectName("");
                  }
                }}
                placeholder="New project name"
                className="flex-1 text-sm text-text-primary bg-surface border border-border rounded px-2 py-1 focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={() => {
                  if (newProjectName.trim()) {
                    onCreateProject(newProjectName.trim());
                    setNewProjectName("");
                  }
                }}
                disabled={!newProjectName.trim()}
                className="text-xs bg-primary text-white rounded px-3 py-1 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notifications Section */}
      <div>
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
          Notifications
        </h3>
        <div className="space-y-1">
          <ToggleField
            label="Daily briefing"
            field="notificationPreferences.dailyBriefing"
            checked={profile.notificationPreferences?.dailyBriefing ?? false}
          />
          <ToggleField
            label="Overdue alerts"
            field="notificationPreferences.overdueAlerts"
            checked={profile.notificationPreferences?.overdueAlerts ?? false}
          />
          <ToggleField
            label="Reminder emails"
            field="notificationPreferences.reminderEmails"
            checked={profile.notificationPreferences?.reminderEmails ?? false}
          />
        </div>
      </div>

      {/* Onboarding Status */}
      {!profile.onboardingComplete && (
        <div className="bg-primary/10 rounded-lg p-3 mt-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-primary flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-xs text-primary">
              Complete your profile to unlock personalized features
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
